import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import { useToast } from "../../components/ui/Toast";
import {
  BlogPostEditor,
  emptyBlogPostFormValues,
  type BlogPostSavePayload,
} from "./blog-edit/BlogPostEditor";
import { CREATE_BLOG_POST } from "./blog-edit/blogPostMutations";
import { GET_NAV_CONFIG } from "./navigation/queries";
import { parseBlogBasePath } from "./blog-edit/parseBlogBasePath";

interface CreateResponse {
  createBlogPost: { id: string; title: string } | null;
}

export function BlogPostCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: navData } = useQuery(GET_NAV_CONFIG, { fetchPolicy: "cache-first" });
  const blogBasePath = parseBlogBasePath(navData?.currentTenant?.featureConfig);
  const [create, createResult] = useMutation<CreateResponse>(CREATE_BLOG_POST, {
    // Drop the cached list so the new post shows up immediately.
    refetchQueries: ["AllBlogPostsForListing"],
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave(payload: BlogPostSavePayload) {
    setSaveError(null);
    try {
      const result = await create({
        variables: {
          title: payload.title,
          slug: payload.slug,
          author: payload.author,
          summary: payload.summary || null,
          body: payload.body || null,
          publishAt: payload.publishAt,
          featuredImageUrl: payload.featuredImageUrl || null,
          featuredImageAlt: payload.featuredImageAlt.trim() || null,
          tagNames: payload.tagNames,
        },
      });
      const created = result.data?.createBlogPost;
      if (!created) throw new Error("Couldn't create post");
      toast.push({
        kind: "success",
        title: `"${created.title}" created`,
        description:
          payload.publishAt == null
            ? "Saved as a draft."
            : new Date(payload.publishAt).getTime() > Date.now()
              ? "Scheduled for publication."
              : "Live on your site.",
      });
      navigate(`/website/blog/${created.id}`, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setSaveError(message);
      toast.push({
        kind: "error",
        title: "Couldn't create post",
        description: message,
      });
    }
  }

  return (
    <BlogPostEditor
      mode="create"
      initialValues={emptyBlogPostFormValues()}
      originalPublishAt={null}
      isSaving={createResult.loading}
      saveError={saveError}
      blogBasePath={blogBasePath}
      onSave={handleSave}
    />
  );
}
